import type { TimelineEvent } from '../hooks/useClientJourney';
import { formatDateUTC3 } from '@/lib/utils';

export interface ClientCycle {
  id: string;
  agendamentoId?: number;
  /** Data de referência do ciclo (agendamento ou contrato órfão) */
  anchorDate: Date;
  label: string;
  events: TimelineEvent[];
  isCurrent: boolean;
}

function eventTime(event: TimelineEvent): number {
  return new Date(event.timestamp).getTime();
}

function agendamentoEvents(events: TimelineEvent[]): TimelineEvent[] {
  return events
    .filter((e) => e.type === 'AGENDAMENTO')
    .sort((a, b) => eventTime(a) - eventTime(b));
}

function contratoEvents(events: TimelineEvent[]): TimelineEvent[] {
  return events.filter((e) => e.type === 'CONTRATO');
}

function otherEvents(events: TimelineEvent[]): TimelineEvent[] {
  return events.filter((e) => e.type !== 'AGENDAMENTO' && e.type !== 'CONTRATO');
}

function cycleLabel(agendamento: TimelineEvent, index: number): string {
  const raw = agendamento.data?.data_agendamento;
  const date = raw ? formatDateUTC3(String(raw)) : formatDateUTC3(agendamento.timestamp);
  const status = agendamento.description;
  return `Atendimento ${index + 1} · ${date}${status ? ` · ${status}` : ''}`;
}

/**
 * Agrupa eventos da jornada do cliente em ciclos de negócio.
 * Cada agendamento inicia um ciclo; contratos posteriores pertencem ao ciclo
 * do agendamento imediatamente anterior (até o próximo agendamento).
 */
export function groupEventsIntoCycles(
  events: TimelineEvent[],
  highlightAgendamentoId?: number
): ClientCycle[] {
  const agendamentos = agendamentoEvents(events);
  const contratos = contratoEvents(events);
  const extras = otherEvents(events);

  if (agendamentos.length === 0) {
    const orphanEvents = [...contratos, ...extras].sort(
      (a, b) => eventTime(b) - eventTime(a)
    );
    if (orphanEvents.length === 0) return [];

    const anchor = orphanEvents.reduce(
      (oldest, e) => (eventTime(e) < eventTime(oldest) ? e : oldest),
      orphanEvents[0]
    );

    return [
      {
        id: 'cycle-orphan',
        anchorDate: new Date(anchor.timestamp),
        label: 'Negócio sem agendamento',
        events: orphanEvents,
        isCurrent: highlightAgendamentoId == null,
      },
    ];
  }

  const cycles: ClientCycle[] = agendamentos.map((agendamento, index) => {
    const agendamentoId = agendamento.data?.id as number | undefined;
    const start = eventTime(agendamento);
    const nextStart =
      index < agendamentos.length - 1 ? eventTime(agendamentos[index + 1]) : Infinity;

    const cycleContratos = contratos.filter((c) => {
      const t = eventTime(c);
      return t >= start && t < nextStart;
    });

    const cycleEvents: TimelineEvent[] = [agendamento, ...cycleContratos].sort(
      (a, b) => eventTime(b) - eventTime(a)
    );

    // Conversão entra no primeiro ciclo (evento único do cliente)
    if (index === 0 && extras.length > 0) {
      cycleEvents.push(...extras);
      cycleEvents.sort((a, b) => eventTime(b) - eventTime(a));
    }

    return {
      id: agendamentoId ? `cycle-${agendamentoId}` : `cycle-${index}`,
      agendamentoId,
      anchorDate: new Date(agendamento.timestamp),
      label: cycleLabel(agendamento, index),
      events: cycleEvents,
      isCurrent: highlightAgendamentoId != null && agendamentoId === highlightAgendamentoId,
    };
  });

  // Contratos anteriores ao primeiro agendamento
  const firstStart = eventTime(agendamentos[0]);
  const orphanContratos = contratos.filter((c) => eventTime(c) < firstStart);
  if (orphanContratos.length > 0) {
    cycles.unshift({
      id: 'cycle-legacy',
      anchorDate: new Date(orphanContratos[0].timestamp),
      label: 'Negócios anteriores',
      events: orphanContratos.sort((a, b) => eventTime(b) - eventTime(a)),
      isCurrent: false,
    });
  }

  if (highlightAgendamentoId == null) {
    const latest = cycles
      .filter((c) => c.agendamentoId != null)
      .sort((a, b) => b.anchorDate.getTime() - a.anchorDate.getTime())[0];
    if (latest) {
      cycles.forEach((c) => {
        c.isCurrent = c.id === latest.id;
      });
    }
  }

  return cycles.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return b.anchorDate.getTime() - a.anchorDate.getTime();
  });
}

export function getCycleStats(cycle: ClientCycle) {
  const agendamentos = cycle.events.filter((e) => e.type === 'AGENDAMENTO').length;
  const contratos = cycle.events.filter((e) => e.type === 'CONTRATO').length;
  const ultima =
    cycle.events.length > 0
      ? cycle.events.reduce(
          (latest, e) => (eventTime(e) > eventTime(latest) ? e : latest),
          cycle.events[0]
        )
      : null;

  const ultimaDisplay = ultima
    ? ultima.type === 'AGENDAMENTO' && ultima.data?.data_agendamento
      ? formatDateUTC3(String(ultima.data.data_agendamento))
      : formatDateUTC3(ultima.timestamp)
    : undefined;

  return {
    agendamentos,
    contratos,
    ultima: ultimaDisplay,
  };
}
